import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Sun, Moon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { taskLogin } from '../../services/taskApi';
import { toast } from 'sonner';

const TaskLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('task_theme') || 'dark');
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('task_theme', next);
  };

  React.useEffect(() => {
    const token = localStorage.getItem('task_token');
    if (token) navigate('/tasks/dashboard');
    try {
      if (sessionStorage.getItem('task_session_expired')) {
        sessionStorage.removeItem('task_session_expired');
        toast.error('Your session expired. Please sign in again.');
      }
    } catch (e) { /* noop */ }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await taskLogin({ email, password });
      localStorage.setItem('task_token', res.data.token);
      localStorage.setItem('task_user', JSON.stringify(res.data.user));
      toast.success(`Welcome, ${res.data.user.name}!`);
      navigate('/tasks/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${
      isDark ? 'bg-gradient-to-br from-slate-900 via-[#1a1f3a] to-slate-900' : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-slate-100'
    }`} data-testid="task-login-page">
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 p-2 rounded-xl transition-all ${
          isDark ? 'text-amber-400/70 hover:bg-white/5' : 'text-gray-500 hover:bg-white/60 bg-white/40'
        }`}
        data-testid="login-theme-toggle"
      >
        {isDark ? <Sun size={20} /> : <Moon size={20} />}
      </button>
      <Card className={`w-full max-w-md border-0 shadow-2xl backdrop-blur-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
        <CardContent className="p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto shadow-lg shadow-blue-500/20">
              <Lock size={24} className="text-white" />
            </div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Task Manager</h1>
            <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Namoh Toolings - Internal Portal</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail size={16} className={`absolute left-3 top-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <Input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Email address" required
                className={`pl-10 ${isDark ? 'bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500'}`}
                data-testid="task-login-email"
              />
            </div>
            <div className="relative">
              <Lock size={16} className={`absolute left-3 top-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <Input
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Password" required
                className={`pl-10 ${isDark ? 'bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500'}`}
                data-testid="task-login-password"
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium h-11" disabled={loading} data-testid="task-login-submit">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskLogin;
